import React, { useState, useEffect } from 'react';
import { X, Mail, Link, Copy, Check, User, Building, Calendar } from 'lucide-react';
import { EmployeeInviteManager } from '../utils/employeeInviteSystem';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import emailService from '../utils/emailService';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const EmployeeInviteModal = ({ isOpen, onClose, currentCompany, selectedEmployee: preSelectedEmployee, onInviteSent }) => {
  const { employees } = useEnhancedCompany();
  const [formData, setFormData] = useState({
    employeeId: '',
    email: '',
    message: ''
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Filter employees who haven't been invited yet or whose invites have expired
  const availableEmployees = (employees || []).filter(emp => 
    emp.status === 'active' && (!emp.inviteStatus || emp.inviteStatus === 'expired' || emp.inviteStatus === 'declined')
  );

  // Handle pre-selected employee from parent component
  useEffect(() => {
    if (preSelectedEmployee && isOpen) {
      setFormData({
        employeeId: preSelectedEmployee.employeeId,
        email: preSelectedEmployee.email || '',
        message: generateInviteMessage(preSelectedEmployee, currentCompany)
      });
      setSelectedEmployee(preSelectedEmployee);
    } else if (!preSelectedEmployee) {
      // Reset form when no pre-selected employee
      setFormData({
        employeeId: '',
        email: '',
        message: ''
      });
      setSelectedEmployee(null);
    }
  }, [preSelectedEmployee, isOpen, currentCompany]);

  useEffect(() => {
    if (formData.employeeId && employees && !preSelectedEmployee) {
      const employee = employees.find(emp => emp.employeeId === formData.employeeId);
      setSelectedEmployee(employee);
      if (employee) {
        setFormData(prev => ({
          ...prev,
          message: generateInviteMessage(employee, currentCompany)
        }));
      }
    } else if (!formData.employeeId && !preSelectedEmployee) {
      setSelectedEmployee(null);
    }
  }, [formData.employeeId, employees, preSelectedEmployee, currentCompany]);

  // Generate invite message template
  const generateInviteMessage = (employee, company) => {
    return `Hi ${employee.name},

You have been invited to join ${company?.firmName || 'our company'} as ${employee.designation || 'an employee'} in the ${employee.department || 'team'}.

Please click on the invite link below to join our team and access your employee dashboard.

Company ID: ${company?.companyId || 'N/A'}
Employee ID: ${employee.employeeId}

We're excited to have you on board!

Best regards,
${company?.firmName || 'Company'} Team`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!formData.employeeId || !formData.email) {
      alert('Please select an employee and provide email address');
      return;
    }

    if (!selectedEmployee) {
      alert('Selected employee not found');
      return;
    }

    setLoading(true);
    
    try {
      const inviteData = {
        employeeId: selectedEmployee.employeeId,
        globalEmployeeId: selectedEmployee.globalEmployeeId,
        name: selectedEmployee.name,
        designation: selectedEmployee.designation,
        department: selectedEmployee.department,
        email: formData.email,
        message: formData.message,
        invitedBy: currentCompany?.userId || 'owner',
        companyId: currentCompany?.companyId
      };

      const result = await EmployeeInviteManager.createEmployeeInvite(
        currentCompany,
        inviteData
      );

      if (result.success) {
        setInviteResult(result);
        
        // Send email via Zoho Mail API
        try {
          console.log('📧 Sending invite email via Zoho Mail API...');
          
          const emailResult = await emailService.sendEmployeeInvite({
            employeeName: selectedEmployee.name,
            employeeEmail: formData.email,
            companyName: currentCompany?.companyName || 'Your Company',
            designation: selectedEmployee.designation || 'Employee',
            department: selectedEmployee.department || 'General',
            inviteLink: result.inviteLink,
            customMessage: formData.message
          });

          if (emailResult.success) {
            console.log('✅ Invite email sent successfully via Zoho');
            setInviteResult(prev => ({ ...prev, emailSent: true, emailMethod: 'zoho' }));
          } else {
            console.error('❌ Zoho email failed, falling back to mailto:', emailResult.error);
            
            // Fallback to mailto if Zoho fails
            const emailSubject = `Invitation to join ${currentCompany?.companyName || 'our company'} on ACCTOO`;
            const emailBody = `Dear ${selectedEmployee.name},

You have been invited to join ${currentCompany?.companyName || 'our company'} as ${selectedEmployee.designation} in the ${selectedEmployee.department} department.

${formData.message}

Please click the link below to accept the invitation:
${result.inviteLink}

This invitation will expire in 7 days.

Best regards,
${currentCompany?.companyName || 'Company'} Team

--
Powered by ACCTOO - Simplifying Accounting, Together
https://app.acctoo.com`;

            const mailtoLink = `mailto:${formData.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
            window.open(mailtoLink, '_blank');
            
            setInviteResult(prev => ({ ...prev, emailSent: false, emailMethod: 'mailto', emailError: emailResult.error }));
          }
        } catch (emailError) {
          console.error('❌ Email sending failed:', emailError);
          setInviteResult(prev => ({ ...prev, emailSent: false, emailError: emailError.message }));
        }
        
        onInviteSent && onInviteSent(result);
      } else {
        alert('Failed to create invite: ' + result.error);
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Error sending invite: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = async () => {
    if (inviteResult?.inviteLink) {
      try {
        await navigator.clipboard.writeText(inviteResult.inviteLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = inviteResult.inviteLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      email: '',
      message: ''
    });
    setSelectedEmployee(null);
    setInviteResult(null);
    setCopied(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-md w-full max-h-[92dvh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {inviteResult ? 'Invite Sent Successfully!' : 'Invite Employee'}
              </h2>
              <p className="text-sm text-gray-500">
                {currentCompany?.firmName || 'Your Company'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {!inviteResult ? (
            /* Invite Form */
            <form onSubmit={handleSendInvite} className="space-y-4">
              {/* Employee Selection */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Employee *
                  </label>
                  <AppSelect
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    placeholder="Choose an employee to invite"
                    aria-label="Select Employee"
                    sheetTitle="Select Employee"
                    disabled={!!preSelectedEmployee}
                    options={[
                      { value: '', label: 'Choose an employee to invite' },
                      ...availableEmployees.map(employee => ({
                        value: employee.employeeId,
                        label: `${employee.name} (${employee.employeeId}) - ${employee.designation || 'No designation'}`,
                      })),
                    ]}
                    triggerClassName={`${APP_SELECT_FILTER_TRIGGER} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  />
                  {availableEmployees.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      No employees available for invitation. Add employees in Employee Management first.
                    </p>
                  )}
                </div>

                {/* Show selected employee details */}
                {selectedEmployee && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Selected Employee Details:</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <span className="ml-2 font-medium">{selectedEmployee.name}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">ID:</span>
                        <span className="ml-2 font-medium">{selectedEmployee.employeeId}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Designation:</span>
                        <span className="ml-2 font-medium">{selectedEmployee.designation || 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Department:</span>
                        <span className="ml-2 font-medium">{selectedEmployee.department || 'Not set'}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="employee@example.com"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The employee will receive the invite link at this email address
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invite Message Template
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="8"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Auto-generated message template..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    You can edit this message. The invite link and company details will be added automatically.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      <span>Send Invite</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Success Result */
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-green-600" />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Invitation Created!
                </h3>
                <div className="text-sm text-gray-600 mb-4 space-y-2">
                  <p>
                    Invitation for <strong>{selectedEmployee?.name}</strong> has been created.
                  </p>
                  
                  {/* Email Status */}
                  {inviteResult.emailSent === true && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-left">
                      <p className="font-medium text-green-900 mb-1">✅ Email Sent Successfully!</p>
                      <p className="text-green-800 text-xs">
                        Professional invitation email has been sent to <strong>{formData.email}</strong> via Zoho Mail.
                      </p>
                    </div>
                  )}
                  
                  {inviteResult.emailSent === false && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-left">
                      <p className="font-medium text-yellow-900 mb-1">⚠️ Email Service Issue</p>
                      <p className="text-yellow-800 text-xs">
                        {inviteResult.emailMethod === 'mailto' 
                          ? '• Your email client should have opened automatically' 
                          : `• Error: ${inviteResult.emailError || 'Unknown error'}`
                        }<br/>
                        • Copy the invite link below and send it manually to: <strong>{formData.email}</strong>
                      </p>
                    </div>
                  )}
                  
                  {inviteResult.emailSent === undefined && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-left">
                      <p className="font-medium text-blue-900 mb-1">📧 Manual Sending Required:</p>
                      <p className="text-blue-800 text-xs">
                        Please copy the invite link below and send it to: <strong>{formData.email}</strong>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Invite Link */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share Invite Link:
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={inviteResult.inviteLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
                  />
                  <button
                    onClick={copyInviteLink}
                    className={`px-3 py-2 rounded-md transition-colors ${
                      copied 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-center space-x-3 pt-4">
                <button
                  onClick={() => {
                    // Re-open email client
                    const emailSubject = `Invitation to join ${currentCompany?.companyName || 'our company'} on ACCTOO`;
                    const emailBody = `Dear ${selectedEmployee?.name},

You have been invited to join ${currentCompany?.companyName || 'our company'} as ${selectedEmployee?.designation} in the ${selectedEmployee?.department} department.

${formData.message}

Please click the link below to accept the invitation:
${inviteResult.inviteLink}

This invitation will expire in 7 days.

Best regards,
${currentCompany?.companyName || 'Company'} Team

--
Powered by ACCTOO - Simplifying Accounting, Together
https://app.acctoo.com`;

                    const mailtoLink = `mailto:${formData.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
                    window.open(mailtoLink, '_blank');
                  }}
                  className="px-4 py-2 text-green-600 bg-green-100 rounded-md hover:bg-green-200 transition-colors flex items-center space-x-2"
                >
                  <Mail className="w-4 h-4" />
                  <span>Open Email Again</span>
                </button>
                <button
                  onClick={resetForm}
                  className="px-4 py-2 text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                >
                  Send Another Invite
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeInviteModal;
